// Sprint UI-1 — single home for path-shape helpers used by the tool-rendering
// pipeline. Previously `longestCommonDir` was duplicated in
// `synthesize-summary.ts` and `aggregate.ts`; both call sites now import
// from here.

/**
 * Longest-common-directory across a list of paths, capped at 2 segments
 * deep so the resulting location clause stays readable. Returns null when
 * there is no shared directory or the paths are all bare basenames.
 */
export function longestCommonDir(paths: string[]): string | null {
  if (paths.length === 0) return null;
  const splitPaths = paths.map((p) => {
    const trimmed = p.replace(/^\.?\//, '');
    const parts = trimmed.split('/').filter(Boolean);
    return parts.slice(0, parts.length - 1); // drop basename / glob leaf
  });
  if (splitPaths.some((p) => p.length === 0)) return null;
  const minLen = Math.min(...splitPaths.map((p) => p.length));
  const common: string[] = [];
  for (let i = 0; i < minLen; i += 1) {
    const seg = splitPaths[0]?.[i];
    if (!seg) break;
    if (splitPaths.every((p) => p[i] === seg)) {
      common.push(seg);
    } else {
      break;
    }
  }
  if (common.length === 0) return null;
  return common.slice(0, 2).join('/');
}
